import { createContext, createSignal, useContext, type JSX } from "solid-js";
import type { Dictionary, Locale } from "./dictionary.ts";
import { dictionaries, fallbackLocale, preferredLocale, storageKey } from "./locale.ts";

export type I18n = {
  locale: () => Locale;
  setLocale: (locale: Locale) => void;
  /** The active dictionary; call it inside JSX so it tracks locale changes. */
  t: () => Dictionary;
};

const I18nContext = createContext<I18n>();

/** Reads and writes the persisted locale; a no-op when storage is unavailable. */
export type LocaleStore = {
  read: () => string | null;
  write: (locale: Locale) => void;
};

export const browserLocaleStore = (): LocaleStore => {
  const storage = typeof localStorage === "undefined" ? undefined : localStorage;

  return {
    read: () => storage?.getItem(storageKey) ?? null,
    write: (locale) => storage?.setItem(storageKey, locale),
  };
};

export const makeI18n = (store: LocaleStore, browserLanguages: readonly string[]): I18n => {
  const [locale, set] = createSignal(preferredLocale(store.read(), browserLanguages));

  return {
    locale,
    setLocale: (next) => {
      store.write(next);
      set(next);
    },
    t: () => dictionaries[locale()],
  };
};

export type I18nProviderProps = {
  value?: I18n;
  children: JSX.Element;
};

export const I18nProvider = (props: I18nProviderProps) => {
  const i18n = props.value ?? makeI18n(browserLocaleStore(), navigator.languages ?? [navigator.language]);
  return <I18nContext.Provider value={i18n}>{props.children}</I18nContext.Provider>;
};

/** Falls back to the default locale when used outside a provider (tests, storybook-style previews). */
export const useI18n = (): I18n => {
  const i18n = useContext(I18nContext);
  if (i18n !== undefined) return i18n;

  return {
    locale: () => fallbackLocale,
    setLocale: () => undefined,
    t: () => dictionaries[fallbackLocale],
  };
};
